/**
 * How a building's name and number read in a group header (the PA team,
 * 2026-10-05: one header said "SEABRANCH HEALTH AND REHABILITATION CENTER"
 * with "7722869440" on a line of its own, the next "Yamato Nursing &
 * Rehabilitation Center").
 *
 * Display only. `facilities.name` is the building's own record and is never
 * rewritten from here; a name somebody typed in mixed case is left exactly as
 * they typed it.
 */

/** Words that stay lower case inside a title (never first). */
const SMALL = new Set(['and', 'of', 'at', 'the', 'for', 'on', 'by', 'in', 'to', 'a', 'an']);
/** Kept upper case: industry acronyms and roman numerals. */
const ACRONYMS = new Set(['ALF', 'SNF', 'LLC', 'ALF/SNF', 'SNF/ALF', 'ILF', 'ACLF', 'LTC', 'CCRC', 'II', 'III', 'IV', 'VA', 'USA', 'HCR', 'PA', 'MC']);

/**
 * Title case for a name typed all in capitals; anything with a lower-case
 * letter in it was typed on purpose and is returned as typed (trimmed, with
 * its spaces collapsed).
 */
export function facilityDisplayName(raw: string | null | undefined): string {
  const name = String(raw || '').trim().replace(/\s+/g, ' ');
  if (!name) return name;
  if (/[a-z]/.test(name)) return name;
  return name.split(' ').map((word, i) => {
    const bare = word.replace(/[^A-Z0-9/]/g, '');
    if (ACRONYMS.has(bare)) return word;
    const lower = word.toLowerCase();
    if (i > 0 && SMALL.has(lower)) return lower;
    // "ST." "MC'S" "O'NEIL" "SEA-BRANCH": capitalise each part after a hyphen or apostrophe-led letter.
    return lower.replace(/(^|[-(/])([a-z])/g, (_m, sep: string, ch: string) => `${sep}${ch.toUpperCase()}`)
      .replace(/^([a-z])/, (ch) => ch.toUpperCase())
      .replace(/'([a-z])\b/g, (_m, ch: string) => `'${ch}`);
  }).join(' ');
}

/**
 * A US number as (xxx) xxx-xxxx, with the dialable form beside it. Eleven
 * digits starting with 1 lose the 1. Anything else (an extension, a short
 * code, a foreign number) is shown as recorded and dialled as digits.
 *
 * Not formatPhone (utils/formatPhone.ts): that takes the first ten digits, so
 * "13523232400" would read "(135) 232-3240" -- a different building.
 */
export function formatFacilityPhone(raw: string | null | undefined): { display: string; tel: string; digits: string } | null {
  const text = String(raw || '').trim();
  if (!text) return null;
  let digits = text.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length === 10) {
    return { display: `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`, tel: `+1${digits}`, digits };
  }
  return { display: text, tel: digits || text, digits };
}
